import EmojiConvertor from 'emoji-js';
import {chain, mapValues, reduce} from 'lodash';
import {CSSProperties} from 'react';

import {makeEnumTypeGuard} from '../../../libs/shared/helpers/src/typescript/enumHelpers';
import {SkinTonesEnum} from '../../models/api/preferencesModel';
import {emojiBaseMap, EmojiCategoriesEnum} from './emojiList';
import {emojiSheetUrl, emojiUrl} from './emojiUrls';

/*
 * Types.
 */

interface SkinVariation {
  readonly id: string;
  readonly sheetX: number;
  readonly sheetY: number;
}

type SkinVariations = {[tone: number]: SkinVariation | undefined};

export interface EmojiSheetPosition {
  readonly sheetX: number;
  readonly sheetY: number;
}

export interface EmojiDefinition extends EmojiSheetPosition {
  readonly id: string;
  readonly shortcode: string;
  readonly shortcodes: ReadonlyArray<string>;
  readonly category: EmojiCategoriesEnum;
  readonly variations?: SkinVariations;
  readonly order: number;
  readonly customUrl?: string;
}

export interface CustomEmojiDefinition extends EmojiDefinition {
  category: EmojiCategoriesEnum.CUSTOM;
  customUrl: string;
}

export type EmojiMap = {[shortCode: string]: EmojiDefinition};

/*
 * Type guard.
 */

export const isEmojiCategoriesEnum = makeEnumTypeGuard(EmojiCategoriesEnum);

export function isCustomEmoji(emoji: EmojiDefinition): emoji is CustomEmojiDefinition {
  return emoji.category === EmojiCategoriesEnum.CUSTOM && emoji.customUrl !== undefined;
}

/*
 * Pre-processing.
 */

export function buildEmojiConvertor() {
  const result = new EmojiConvertor();

  result.img_sets.apple.path = emojiUrl;
  result.img_sets.apple.sheet = emojiSheetUrl;
  result.use_sheet = true;
  result.img_set = 'apple';

  return result;
}

function buildEmojiMap(): EmojiMap {
  const convertor = buildEmojiConvertor();
  return mapValues(emojiBaseMap, (emojiBase, shortcode) => {
    const emoji: EmojiDefinition = {
      id: emojiBase.u,
      shortcode,
      shortcodes: emojiBase.s,
      category: emojiBase.c,
      variations: findSkinVariation(convertor, emojiBase.u),
      order: emojiBase.o,
      sheetX: emojiBase.x,
      sheetY: emojiBase.y,
    };
    return emoji;
  });
}

type SkinTonesMap = {[id: string]: SkinTonesEnum | undefined};
const skinTonesMap: SkinTonesMap = {
  '1f3fb': SkinTonesEnum.LIGHT,
  '1f3fc': SkinTonesEnum.MEDIUM_LIGHT,
  '1f3fd': SkinTonesEnum.MEDIUM,
  '1f3fe': SkinTonesEnum.MEDIUM_DARK,
  '1f3ff': SkinTonesEnum.DARK,
};

function findSkinVariation(convertor: EmojiConvertor, emojiId: string) {
  // Find the variation data for this emoji.
  const variationsData = convertor.variations_data[emojiId];
  if (!variationsData) {
    return undefined;
  }

  // If one was found, create the SkinVariations.
  return reduce(
    variationsData,
    (result, value, key) => {
      // Find the corresponding skin tone.
      const skinTone = skinTonesMap[key];
      if (!skinTone || !value) {
        return result;
      }

      // If one was found, add it to the result.
      const [id, sheetX, sheetY] = value;
      result[skinTone] = {id, sheetX, sheetY};
      return result;
    },
    {} as SkinVariations,
  );
}

/* eslint no-param-reassign: 0 */
export function buildEmojiValidationMap(baseMap: EmojiMap): EmojiMap {
  return chain(baseMap)
    .values()
    .reduce(
      (result, emoji) =>
        emoji.shortcodes.reduce((subResult, shortCode) => {
          subResult[shortCode] = emoji;
          return subResult;
        }, result),
      {} as EmojiMap,
    )
    .value();
}

type SheetCoordinateTransform = (coordinates: EmojiSheetPosition) => CSSProperties;
function buildSheetCoordinateCssBuilder(): SheetCoordinateTransform {
  // Extract sizing constants from the convertor.
  const convertor = buildEmojiConvertor();
  const imgSetSheetSize = convertor.img_sets[convertor.img_set].sheet_size;
  const sheetSize = convertor.sheet_size * (imgSetSheetSize + 2);

  // Build the constants used to build the CSS properties.
  const divider = sheetSize - imgSetSheetSize;
  const multiplier = imgSetSheetSize + 2;
  const sheetZoom = 100 * (sheetSize / imgSetSheetSize);

  return ({sheetX, sheetY}) => {
    const sheetXPercent = 100 * ((sheetX * multiplier + 1) / divider);
    const sheetYPercent = 100 * ((sheetY * multiplier + 1) / divider);

    return {
      background: `url(${emojiSheetUrl})`,
      backgroundPosition: `${sheetXPercent}% ${sheetYPercent}%`,
      backgroundSize: `${sheetZoom}% ${sheetZoom}%`,
    };
  };
}

const baseEmojiMap = buildEmojiMap();

/*
 * Exports.
 */

export const baseEmojiList = chain(baseEmojiMap)
  .values()
  .orderBy([(e) => e.category, (e) => e.order])
  .value();

// Use getEmojiMap() selector for custom emoji support
export const emojiMap = baseEmojiMap;
export const buildSheetCoordinateCss = buildSheetCoordinateCssBuilder();
export const baseEmojiValidationMap = buildEmojiValidationMap(baseEmojiMap);
